import { useAnalytics } from '@/composables/analytics'
import {
  CALENDAR_EVENT_ACTIONS,
  CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME,
  CALENDAR_SETTINGS_ANALYTICS_TRANSLATED_VALUES,
} from './consts.js'

import { CALENDAR_SETTINGS } from '@/components/calendar/consts.js'

const _translateToValueRequiredByAnalytics = (value) => {
  return CALENDAR_SETTINGS_ANALYTICS_TRANSLATED_VALUES[value] || null
}

export function useCalendarAnalyticsEvents () {
  const analytics = useAnalytics()
  const { EVENTS, EVENT_ACTIONS: GLOBAL_EVENT_ACTIONS } = analytics

  const EVENT_ACTIONS = {
    ...GLOBAL_EVENT_ACTIONS,
    ...CALENDAR_EVENT_ACTIONS,
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143223850/screen+name+calendar+view+dropdown
  const sendEventAboutCalendarViewChange = (oldView, newView, source) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW_DROPDOWN,
      eventAction: EVENT_ACTIONS.PROCEED_CLICKED,
      oldView: _translateToValueRequiredByAnalytics(oldView),
      newView: _translateToValueRequiredByAnalytics(newView),
      actionSource: source || 'dropdown',
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutDatepickerOnCalendarSidebar = (jumpByWeek) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: jumpByWeek ? EVENT_ACTIONS.JUMP_CLICKED : EVENT_ACTIONS.DATE_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197061633/screen+name+calendar+datepicker
  const sendEventAboutDatepickerOnCalendarHeader = (jumpByWeek) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.DATEPICKER,
      eventAction: jumpByWeek ? EVENT_ACTIONS.JUMP_CLICKED : EVENT_ACTIONS.DATE_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197061633/screen+name+calendar+datepicker
  const sendEventAboutDatepickerArrowOnCalendarHeader = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.DATEPICKER,
      eventAction: EVENT_ACTIONS.ARROW_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutToggleSidebarFilters = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_SIDEBAR_ICON_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutRedirectToCalendarSettings = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.SETTINGS_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143223850/screen+name+calendar+view+dropdown
  const sendEventAboutOpeningViewPicker = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW_DROPDOWN,
      eventAction: EVENT_ACTIONS.VIEW_OPENED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143223850/screen+name+calendar+view+dropdown
  const sendEventAboutClosingViewPicker = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW_DROPDOWN,
      eventAction: EVENT_ACTIONS.CANCEL_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutTodayButtonClick = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.TODAY_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutOpeningSearchCustomerDrawer = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_CLIENT_SEARCH_CLICKED,
    })
  }
  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutNotificationIconClick = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_NOTIFICATION_ICON_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutNewAppointmentFromAddEventDropdown = (openingSource) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_NEW_APPOINTMENT,
      actionSource: _translateToValueRequiredByAnalytics(openingSource),
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutNewTimeReservationFromAddEventDropdown = (openingSource) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_NEW_TIME_RESERVATION,
      actionSource: _translateToValueRequiredByAnalytics(openingSource),
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutAddTimeOffAddEventDropdown = (openingSource) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_ADD_TIME_OFF,
      actionSource: _translateToValueRequiredByAnalytics(openingSource),
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutEventCardClick = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_EVENT_CARD_CLICK,
    })
  }

  const _hasBeenAssignedToOtherResource = (orginalAppointment, editedBooking) => {
    const subbooking = orginalAppointment.subbookings?.find(subbooking => subbooking.id === editedBooking.id)

    if (subbooking) {
      if (editedBooking.staffer_id) return subbooking.staffer?.id !== editedBooking?.staffer_id
      if (editedBooking.appliance_id) return subbooking.appliance?.id !== editedBooking?.appliance_id

      return false
    }

    return false
  }

  const _bookingTimeHasBeenChanged = (orginalAppointment, editedBooking) => {
    const subbooking = orginalAppointment.subbookings?.find(subbooking => subbooking.id === editedBooking.id)
    return subbooking?.booked_from !== editedBooking.booked_from
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutCardEventChangedByDragging = (data) => {
    if (!data.orginalAppointment && !data.editedBooking) return

    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_EVENT_CARD_CHANGED,
      resourceChanged: _hasBeenAssignedToOtherResource(data.orginalAppointment, data.editedBooking),
      timeChanged: _bookingTimeHasBeenChanged(data.orginalAppointment, data.editedBooking),
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutCardEventChangeCanceledAfterDragging = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_EVENT_CARD_CHANGE_CANCELED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutApptOverbookingConfirm = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.APPT_OVERBOOKING_CONFIRMED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197094401/screen+name+calendar+view
  const sendEventAboutApptOverbookingCancel = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.APPT_OVERBOOKING_CANCELED,
    })
  }

  const sendEventAboutEventCardClickOnApptView = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.APPT_LIST,
      eventAction: EVENT_ACTIONS.CALENDAR_EVENT_CARD_CLICK,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2506883075/screen+name+calendar+notification
  const sendEventAboutOpeningNotificationDrawer = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.CALENDAR_NOTIFICATION,
      eventAction: EVENT_ACTIONS.VIEW_OPENED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2506883075/screen+name+calendar+notification
  const sendEventAboutClosingNotificationDrawer = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.CALENDAR_NOTIFICATION,
      eventAction: EVENT_ACTIONS.BACK_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2506883075/screen+name+calendar+notification
  const sendEventAboutNotificationClick = (notificationModel = {}) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.CALENDAR_NOTIFICATION,
      eventAction: EVENT_ACTIONS.PROCEED_CLICKED,
      notificationId: notificationModel.notif_id,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2506883075/screen+name+calendar+notification
  const sendEventAboutNotificationViewed = (notificationModel = {}) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.CALENDAR_NOTIFICATION,
      eventAction: EVENT_ACTIONS.CALENDAR_NOTIFICATION_VIEWED,
      notificationId: notificationModel.notif_id,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2506883075/screen+name+calendar+notification
  const sendEventAboutNotificationFiltersClick = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.CALENDAR_NOTIFICATION,
      eventAction: EVENT_ACTIONS.NOTIFICATIONS_FILTERS_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutOpeningCalendarSettingsView = (calendarSettingsModel = {}) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.VIEW_OPENED,
      defaultView: _translateToValueRequiredByAnalytics(calendarSettingsModel[CALENDAR_SETTINGS.DEFAULT_VIEW]),
      displayMode: _translateToValueRequiredByAnalytics(calendarSettingsModel[CALENDAR_SETTINGS.FIT_WIDTH]),
      colorSheme: _translateToValueRequiredByAnalytics(calendarSettingsModel[CALENDAR_SETTINGS.SERVICE_COLOR_PALETTE]),
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutSaveCalendarSettings = (calendarSettingsModel = {}) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.PROCEED_CLICKED,
      defaultView: _translateToValueRequiredByAnalytics(calendarSettingsModel[CALENDAR_SETTINGS.DEFAULT_VIEW]),
      displayMode: _translateToValueRequiredByAnalytics(calendarSettingsModel[CALENDAR_SETTINGS.FIT_WIDTH]),
      colorSheme: _translateToValueRequiredByAnalytics(calendarSettingsModel[CALENDAR_SETTINGS.SERVICE_COLOR_PALETTE]),
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutBackFromCalendarSettings = (calendarSettingsModel = {}) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.BACK_CLICKED,
      defaultView: _translateToValueRequiredByAnalytics(calendarSettingsModel[CALENDAR_SETTINGS.DEFAULT_VIEW]),
      displayMode: _translateToValueRequiredByAnalytics(calendarSettingsModel[CALENDAR_SETTINGS.FIT_WIDTH]),
      colorSheme: _translateToValueRequiredByAnalytics(calendarSettingsModel[CALENDAR_SETTINGS.SERVICE_COLOR_PALETTE]),
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutStartImportingCalendarEvents = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.CALEMDAR_IMPORTER_FILE_IMPORT,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutDismissImportingCalendarEvents = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.CALENDAR_IMPORTER_FILE_DISSMIS,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutErrorToastMessageForImporter = (toastMessage) => {
    const _toastMessage = _translateToValueRequiredByAnalytics(toastMessage)

    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.TOAST_MESSAGE,
      toastMessage: _toastMessage,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutFileSelectForImporter = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.CALENDAR_IMPORTER_FILE_SELECT,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutOpeningHintsModalForImporter = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.CALENDAR_IMPORTER_NEED_HELP,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutOpeningTutorialLinkForImporter = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.CALENDAR_IMPORTER_TUTORIAL,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2143354918/screen+name+calendar+settings
  const sendEventAboutRedirectToShiftsView = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.SETTINGS,
      eventAction: EVENT_ACTIONS.GO_TO_SHIFTS_CLICKED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197061662/screen+name+calendar+resource+menu
  const sendEventAboutOpeningCalendarHeadlineResourceMenu = () => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.RESOURCE_MENU,
      eventAction: EVENT_ACTIONS.VIEW_OPENED,
    })
  }

  // https://booksy.atlassian.net/wiki/spaces/MARTECH/pages/2197061662/screen+name+calendar+resource+menu
  const sendEventAboutSelectingCalendarHeadlineResourceMenuOption = (optionChoosen) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.RESOURCE_MENU,
      eventAction: EVENT_ACTIONS.PROCEED_CLICKED,
      optionChoosen: _translateToValueRequiredByAnalytics(optionChoosen),
    })
  }

  const sendEventAboutOutdatedCalendar = (duration) => {
    analytics.dispatch(EVENTS.CALENDAR_SETINGS_ACTION, {
      screenName: CALENDAR_SETTINGS_ANALYTICS_SCREEN_NAME.VIEW,
      eventAction: EVENT_ACTIONS.CALENDAR_DATA_UPDATED,
      outdatedDuration: duration,
    })
  }

  return {
    sendEventAboutCalendarViewChange,
    sendEventAboutDatepickerOnCalendarSidebar,
    sendEventAboutDatepickerOnCalendarHeader,
    sendEventAboutDatepickerArrowOnCalendarHeader,
    sendEventAboutToggleSidebarFilters,
    sendEventAboutRedirectToCalendarSettings,
    sendEventAboutOpeningViewPicker,
    sendEventAboutClosingViewPicker,
    sendEventAboutTodayButtonClick,
    sendEventAboutOpeningSearchCustomerDrawer,
    sendEventAboutNotificationIconClick,
    sendEventAboutNewAppointmentFromAddEventDropdown,
    sendEventAboutNewTimeReservationFromAddEventDropdown,
    sendEventAboutAddTimeOffAddEventDropdown,
    sendEventAboutEventCardClick,
    sendEventAboutCardEventChangedByDragging,
    sendEventAboutCardEventChangeCanceledAfterDragging,
    sendEventAboutApptOverbookingConfirm,
    sendEventAboutApptOverbookingCancel,
    sendEventAboutEventCardClickOnApptView,
    sendEventAboutOpeningNotificationDrawer,
    sendEventAboutClosingNotificationDrawer,
    sendEventAboutNotificationClick,
    sendEventAboutNotificationViewed,
    sendEventAboutNotificationFiltersClick,
    sendEventAboutOpeningCalendarSettingsView,
    sendEventAboutSaveCalendarSettings,
    sendEventAboutBackFromCalendarSettings,
    sendEventAboutStartImportingCalendarEvents,
    sendEventAboutDismissImportingCalendarEvents,
    sendEventAboutErrorToastMessageForImporter,
    sendEventAboutFileSelectForImporter,
    sendEventAboutOpeningHintsModalForImporter,
    sendEventAboutOpeningTutorialLinkForImporter,
    sendEventAboutRedirectToShiftsView,
    sendEventAboutOpeningCalendarHeadlineResourceMenu,
    sendEventAboutSelectingCalendarHeadlineResourceMenuOption,
    sendEventAboutOutdatedCalendar,
  }
}
